import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Renders a string that mixes plain (Bangla/English) text with LaTeX math
 * delimited by `$...$` (inline) or `$$...$$` (display).
 *
 * Question-bank content stores math as raw TeX, e.g.
 *   "$\angle \mathrm{ACB}=90^\circ$ হলে, প্রমাণ কর যে, ..."
 * KaTeX renders synchronously to HTML+CSS spans, so — unlike MathJax — the
 * output is captured cleanly by html2canvas when the paper is exported to PDF.
 * We force `output: 'html'` (no MathML twin) so html2canvas doesn't paint the
 * visually-hidden MathML on top of the visual math.
 */

// Split text into ordered segments of plain text and TeX.
function splitSegments(input) {
    const segments = [];
    // $$...$$ (display) is tried before $...$ (inline) via alternation order.
    const re = /\$\$([\s\S]+?)\$\$|\$([^$]+?)\$/g;
    let last = 0;
    let m;
    while ((m = re.exec(input)) !== null) {
        if (m.index > last) segments.push({ type: 'text', value: input.slice(last, m.index) });
        if (m[1] != null) segments.push({ type: 'block', value: m[1] });
        else segments.push({ type: 'inline', value: m[2] });
        last = re.lastIndex;
    }
    if (last < input.length) segments.push({ type: 'text', value: input.slice(last) });
    return segments;
}

// Math-mode-only tokens that must NOT appear inside \text{...}. The question
// bank sometimes wraps real math in \text{} (e.g. "\text{m \neq n}",
// "\text{m^{2}+...}"), which makes KaTeX error out. When a \text body contains
// any of these, we unwrap it so the content renders as math instead.
const MATH_IN_TEXT = /[\^_]|\\(neq|leq|geq|leqslant|geqslant|times|div|pm|mp|cdot|cdots|ldots|frac|sqrt|circ|angle|mathrm|mathbf|left|right|sum|prod|int|infty|rightarrow|Rightarrow|leftarrow|alpha|beta|gamma|delta|theta|lambda|mu|sigma|omega|pi|in|notin|subset|supset|cup|cap|forall|exists|partial|nabla|vec|hat|bar|overline|approx|equiv|propto|sim|cong|geq|le|ge|ne)\b/;

// Match \text{...} allowing one level of nested braces (e.g. m^{2}).
const TEXT_WRAP = /\\text\s*\{((?:[^{}]|\{[^{}]*\})*)\}/g;

function preprocessTex(tex) {
    return tex.replace(TEXT_WRAP, (full, body) => (MATH_IN_TEXT.test(body) ? body : full));
}

// Exported so other modules (AI documents) render TeX with the SAME KaTeX settings.
export function renderTex(tex, displayMode) {
    try {
        return katex.renderToString(preprocessTex(tex), {
            throwOnError: false,
            errorColor: '#dc2626',
            displayMode,
            output: 'html',
        });
    } catch {
        return null;
    }
}

export default function MathText({ text, className }) {
    const str = text == null ? '' : String(text);
    // Fast path: no math delimiters → render as-is.
    if (!str.includes('$')) return className ? <span className={className}>{str}</span> : str;

    const segments = splitSegments(str);
    const content = segments.map((seg, i) => {
        if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
        const html = renderTex(seg.value, seg.type === 'block');
        // If KaTeX fails outright, fall back to the raw TeX so nothing is lost.
        if (html == null) return <span key={i}>{seg.value}</span>;
        // KaTeX's base `.katex` sets no display/white-space, so inline math can
        // wrap mid-expression (e.g. a fraction splitting numerator/denominator
        // across a line break). Render each inline run as a non-breaking
        // inline-block so the whole expression stays intact; line breaks still
        // happen at the plain text/`+` between separate `$...$` segments.
        const style = seg.type === 'inline'
            ? { display: 'inline-block', whiteSpace: 'nowrap' }
            : undefined;
        return <span key={i} style={style} dangerouslySetInnerHTML={{ __html: html }} />;
    });

    return <span className={className}>{content}</span>;
}
